import React from 'react';
import classnames from 'classnames';
import { ToggleGroup, ToggleGroupItem } from '@ohif/ui-next';
import { Icons } from '@ohif/ui-next';
import { actionIcon, viewPreset } from './types';

function PanelStudyBrowserHeader({
  seriesSummary,
  viewPresets,
  updateViewPresetValue,
  actionIcons,
  updateActionIconValue,
  toolbarOnly = false,
  showStudyPreviews = false,
  onToggleStudyPreviews,
}: {
  seriesSummary: { seriesCount: number; imageCount: number; studyCount?: number };
  viewPresets: viewPreset[];
  updateViewPresetValue: (viewPreset: viewPreset) => void;
  actionIcons: actionIcon[];
  updateActionIconValue: (actionIcon: actionIcon) => void;
  toolbarOnly?: boolean;
  showStudyPreviews?: boolean;
  onToggleStudyPreviews?: () => void;
}) {
  return (
    <div
      className={classnames(
        'border-viewer-border flex select-none items-center border-b bg-white px-2',
        toolbarOnly ? 'min-h-[40px] justify-end py-1.5' : 'h-[52px]'
      )}
    >
      {!toolbarOnly && (
        <div className="min-w-0 flex-1">
          <div className="text-viewer-breadcrumb-page text-[11px] font-bold tracking-[0.35px]">
            Study Series
          </div>
          <div className="text-viewer-ink-muted text-[10px] font-normal leading-[15px]">
            {showStudyPreviews ? (
              <>
                {seriesSummary.studyCount ?? 0} Studies
                <span className="text-viewer-ink-muted px-1.5">•</span>
                {seriesSummary.imageCount} Images
              </>
            ) : (
              <>
                {seriesSummary.seriesCount} Series
                <span className="text-viewer-ink-muted px-1.5">•</span>
                {seriesSummary.imageCount} Images
              </>
            )}
          </div>
        </div>
      )}
      <div className="flex flex-shrink-0 items-center gap-2">
        {onToggleStudyPreviews ? (
          <button
            type="button"
            className={classnames(
              'rounded px-2 py-1 text-[10px] font-semibold uppercase tracking-wide',
              showStudyPreviews
                ? 'bg-[#0f91b2]/15 text-[#0f91b2]'
                : 'text-[#737373] hover:text-[#0f91b2]'
            )}
            onClick={onToggleStudyPreviews}
            title={showStudyPreviews ? 'Show series list' : 'Show study previews'}
          >
            {showStudyPreviews ? 'Studies' : 'Series'}
          </button>
        ) : null}
        {!showStudyPreviews ? (
          <>
            <div className="flex items-center gap-1 text-[#737373]">
              {actionIcons.map((icon: actionIcon, index) =>
                React.createElement(Icons[icon.iconName] || Icons.MissingIcon, {
                  key: index,
                  onClick: () => updateActionIconValue(icon),
                  className: 'h-5 w-5 cursor-pointer hover:text-[#0f91b2]',
                })
              )}
            </div>
            <div className="panel-study-browser-view-toggle">
              <ToggleGroup
                type="single"
                className="!bg-neutral-100/90"
                value={viewPresets.filter(preset => preset.selected)[0]?.id}
                onValueChange={value => {
                  const selectedViewPreset = viewPresets.find(preset => preset.id === value);
                  updateViewPresetValue(selectedViewPreset);
                }}
              >
                {viewPresets.map((viewPreset: viewPreset, index) => (
                  <ToggleGroupItem
                    key={index}
                    aria-label={viewPreset.id}
                    value={viewPreset.id}
                    className={
                      'hover:!bg-neutral-200/60 data-[state=on]:!bg-[#0f91b2]/15 ' +
                      '[&_svg]:h-[18px] [&_svg]:w-[18px] [&_svg]:shrink-0'
                    }
                  >
                    {React.createElement(Icons[viewPreset.iconName] || Icons.MissingIcon, {
                      className: 'h-[18px] w-[18px]',
                    })}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

export { PanelStudyBrowserHeader };
